import { Component, ChangeDetectionStrategy, ElementRef, AfterViewInit, OnDestroy, ChangeDetectorRef, HostListener, ViewChild, HostBinding, Input, Optional } from '@angular/core';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { ModalPageMarker } from 'providers/modal-page-marker';

@Component({
  selector: 'cs-header-title',
  templateUrl: 'cs-header-title.html',
  host: { 'class': 'header-title' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsHeaderTitle implements AfterViewInit, OnDestroy {
  @ViewChild('titleText', { static: false }) titleTextElement: ElementRef;

  @Input() wrap: boolean = false;
  @Input() small: boolean = false;

  @HostBinding('attr.wrap') get wrapAttr() {
    return this.wrap ? '' : null;
  }

  @HostBinding('class.truncated') isTruncated = false;

  @HostBinding('class.small') get isSmall() {
    return this.small || !!this.modalPageMarker || this.isInsidePopover;
  }

  private get isInsidePopover(): boolean {
    return !!this.elementRef.nativeElement.closest('popover-host');
  }

  showTooltip = false;
  private resizeObserver: ResizeObserver;
  private tooltipElement: HTMLElement | null = null;
  private hideTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private longPressTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private longPressTriggered = false;
  private readonly LONG_PRESS_DURATION = 500; // ms
  private clickCaptureHandler: (event: Event) => void;

  constructor(
    private elementRef: ElementRef,
    private cd: ChangeDetectorRef,
    @Optional() private modalPageMarker: ModalPageMarker
  ) {}

  ngAfterViewInit() {
    // check truncation on resize
    this.resizeObserver = new ResizeObserver(() => {
      this.checkTruncation();
    });
    this.resizeObserver.observe(this.elementRef.nativeElement);

    // initial check
    this.checkTruncation();

    // use capture phase to intercept clicks before they reach child elements
    this.clickCaptureHandler = (event: Event) => {
      if (this.longPressTriggered) {
        event.stopPropagation();
        event.preventDefault();
        this.longPressTriggered = false;
      }
    };
    this.elementRef.nativeElement.addEventListener('click', this.clickCaptureHandler, true);
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.clickCaptureHandler) {
      this.elementRef.nativeElement.removeEventListener('click', this.clickCaptureHandler, true);
    }
    this.clearLongPressTimeout();
    this.clearHideTimeout();
    this.removeTooltip();
  }

  private checkTruncation() {
    if (!this.titleTextElement) {
      return;
    }
    const textElement = this.titleTextElement.nativeElement as HTMLElement;
    const wasTruncated = this.isTruncated;
    this.isTruncated = textElement.scrollWidth > textElement.clientWidth;

    if (wasTruncated !== this.isTruncated) {
      this.cd.markForCheck();
    }
  }

  // Desktop: show on hover
  @HostListener('mouseenter')
  onMouseEnter() {
    if (this.isTruncated && !hasClientSmallScreen()) {
      this.clearHideTimeout();
      this.showTooltipElement();
    }
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    if (!hasClientSmallScreen()) {
      // delay hiding to allow moving to tooltip
      this.hideTimeoutId = setTimeout(() => {
        this.hideTooltipElement();
      }, 100);
    }
  }

  // Mobile: long-press to show tooltip (allows tap for navigation)
  @HostListener('touchstart')
  onTouchStart() {
    // reset flag on new touch
    this.longPressTriggered = false;

    if (this.isTruncated && hasClientSmallScreen()) {
      this.longPressTimeoutId = setTimeout(() => {
        this.longPressTriggered = true;
        this.showTooltipElement();
        // auto-hide after delay
        this.hideTimeoutId = setTimeout(() => {
          this.hideTooltipElement();
        }, 3000);
      }, this.LONG_PRESS_DURATION);
    }
  }

  @HostListener('touchend')
  @HostListener('touchcancel')
  onTouchEnd() {
    this.clearLongPressTimeout();
  }

  private clearLongPressTimeout() {
    if (this.longPressTimeoutId) {
      clearTimeout(this.longPressTimeoutId);
      this.longPressTimeoutId = null;
    }
  }

  private showTooltipElement() {
    if (this.showTooltip) {
      return;
    }
    this.showTooltip = true;
    this.createTooltip();
  }

  private hideTooltipElement() {
    this.showTooltip = false;
    this.removeTooltip();
  }

  private clearHideTimeout() {
    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }
  }

  private createTooltip() {
    if (!this.titleTextElement) {
      return;
    }
    const textElement = this.titleTextElement.nativeElement as HTMLElement;
    const rect = textElement.getBoundingClientRect();

    // create tooltip element
    this.tooltipElement = document.createElement('div');
    this.tooltipElement.className = 'cs-header-title-tooltip';
    this.tooltipElement.textContent = textElement.textContent?.trim() || '';

    // position below the title
    this.tooltipElement.style.position = 'fixed';
    this.tooltipElement.style.top = `${rect.bottom + 8}px`;
    this.tooltipElement.style.left = `${rect.left}px`;
    this.tooltipElement.style.maxWidth = `${Math.min(rect.width + 100, window.innerWidth - 32)}px`;
    this.tooltipElement.style.zIndex = '10000';

    // allow hovering over tooltip on desktop
    this.tooltipElement.addEventListener('mouseenter', () => {
      this.clearHideTimeout();
    });
    this.tooltipElement.addEventListener('mouseleave', () => {
      if (!hasClientSmallScreen()) {
        this.hideTooltipElement();
      }
    });

    document.body.appendChild(this.tooltipElement);
  }

  private removeTooltip() {
    if (this.tooltipElement && this.tooltipElement.parentElement) {
      this.tooltipElement.parentElement.removeChild(this.tooltipElement);
      this.tooltipElement = null;
    }
  }
}